import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMarkAllNotificationsReadMutation } from '../api/use-mark-all-notifications-read-mutation';
import { useMarkNotificationReadMutation } from '../api/use-mark-notification-read-mutation';
import { openPortalNotification } from '../utils/openPortalNotification';
import { canMarkPortalNotificationRead } from '../utils/notificationReadActions';
import {
  isPortalNotificationUnread,
  normalizePortalNotificationSource,
} from '../utils/portalNotificationSource';
import { useAuth } from '../contexts/AuthContext';

/** Shared mark-as-read + open navigation for inbox, FCM, and socket notifications. */
export const usePortalNotificationActions = () => {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const markRead = useMarkNotificationReadMutation();
  const markAllRead = useMarkAllNotificationsReadMutation();

  const markAsRead = useCallback(
    (source) => {
      const normalized = normalizePortalNotificationSource(source);
      if (!isPortalNotificationUnread(normalized) || !canMarkPortalNotificationRead(normalized)) {
        return;
      }
      markRead.mutate(source);
    },
    [markRead],
  );

  const openNotification = useCallback(
    (source) =>
      openPortalNotification(source, {
        userRole,
        navigate,
        markRead,
      }),
    [markRead, navigate, userRole],
  );

  return useMemo(
    () => ({
      markAsRead,
      openNotification,
      markRead,
      markAllRead,
    }),
    [markAllRead, markAsRead, markRead, openNotification],
  );
};

export default usePortalNotificationActions;
